import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Search,
  Eye,
  Trash2,
  Plus,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";

const EmployeeDetailListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const navigate = useNavigate();

  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const pageSize = 10;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchName = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchName,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchName, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const fetchBranches = async () => {
    try {
      const res = await axios.get("https://nissi-india.com/api/getBranchDropdown", {
        params: {
          branchid: branchId,
          branchtype: branchName,
        },
        headers: commonHeaders,
      });

      const branches = res.data?.branches || [];
      setBranchOptions(branches);

      if (branches.length > 0) {
        const defaultBranch =
          branches.find(
            (b) =>
              String(b.branchid || b.fld_Branchid || "") === String(branchId)
          ) || branches[0];

        setSelectedBranch(
          String(
            defaultBranch.branchid ||
              defaultBranch.fld_Branchid ||
              defaultBranch.branchname ||
              defaultBranch.fld_BranchName ||
              ""
          )
        );
      }
    } catch (err) {
      console.error("Failed to load branches:", err);
      toast.error("Failed to load branches");
      setBranchOptions([]);
    }
  };

  const fetchEmployees = async (targetPage = page) => {
    try {
      setLoading(true);
      const res = await axios.get("https://nissi-india.com/api/getEmployeeDetails", {
        params: {
          branch: selectedBranch || "%",
          empcode: employeeCode || "%",
          empname: employeeName || "%",
          page: targetPage,
          pageSize,
        },
        headers: commonHeaders,
      });

      setEmployees(
        Array.isArray(res.data?.employeeList) ? res.data.employeeList : []
      );
      setTotalRecords(res.data?.totalRecords || 0);
    } catch (err) {
      console.error("Failed to load employee data:", err);
      toast.error("Failed to load employee data");
      setEmployees([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, [commonHeaders]);

  useEffect(() => {
    if (selectedBranch !== "") {
      fetchEmployees(page);
    }
  }, [selectedBranch, page]);

  const handleSearch = () => {
    setPage(1);
    fetchEmployees(1);
  };

  const handleViewAll = () => {
    setEmployeeCode("");
    setEmployeeName("");
    setPage(1);
    fetchEmployees(1);
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleEdit = (empid) => {
    navigate(`/inventory/EmployeeDetail-Add?empid=${empid}`);
  };

const handleDeleteClick = () => {
  if (selectedIds.length === 0) {
    toast.warning("Please select at least one employee");
    return;
  }
  setShowDeleteConfirm(true);
};

const confirmDelete = async () => {
  setShowDeleteConfirm(false);

  try {
    const res = await axios.delete("https://nissi-india.com/api/deleteEmployee", {
      data: { ids: selectedIds },
      headers: commonHeaders,
    });

    if (res.data?.success) {
      toast.success(res.data.message || "Deleted successfully");
      setSelectedIds([]);
      fetchEmployees(page);
    } else {
      toast.error(res.data?.message || "Delete failed");
    }
  } catch (err) {
    console.error("Delete failed:", err);
    toast.error(
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      "Failed to delete employee(s)"
    );
  }
};

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Employee Detail Listing</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Filters Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Employee Details</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Search, view and manage employees
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className={labelClass}>Employee Code</label>
              <input
                type="text"
                placeholder="Enter code"
                className={inputClass}
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value)}
              />
            </div>

            <div>
              <label className={labelClass}>Employee Name</label>
              <input
                type="text"
                placeholder="Enter name"
                className={inputClass}
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
              />
            </div>

            <div>
              <label className={labelClass}>Branch</label>
              <select
                className={inputClass}
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
              >
                <option value="">Select Branch</option>
                {branchOptions.map((b, i) => {
                  const value = b.branchid || b.fld_Branchid || "";
                  const label = b.branchname || b.fld_BranchName || value;
                  return (
                    <option key={value || i} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <Search size={16} />
                Search
              </button>
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={handleViewAll}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Eye size={16} />
                View All
              </button>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Employee List</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {totalRecords} records
                </span>
              </div>

              <div className="flex items-center gap-3">
<button
  onClick={handleDeleteClick}
  disabled={selectedIds.length === 0}
  className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition ${
    selectedIds.length > 0
      ? "bg-red-600 hover:bg-red-700"
      : "bg-gray-400 cursor-not-allowed"
  }`}
>
  <Trash2 size={16} />
  Delete ({selectedIds.length})
</button>

                <button
                  onClick={() => navigate("/inventory/EmployeeDetail-Add")}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-green-600 hover:bg-green-700`}
                >
                  <Plus size={16} />
                  Add New
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Employee Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Employee Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Designation</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Branch</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Action</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      Loading employees...
                    </td>
                  </tr>
                ) : employees.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Users size={40} className="text-gray-400" />
                        <span>No employees found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  employees.map((emp, index) => {
                    const empId = emp.empid || emp.fld_employeeid;
                    const isSelected = selectedIds.includes(empId);

                    return (
                      <tr
                        key={empId || index}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        } ${isSelected ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20` : ""}`}
                      >
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleCheckboxChange(empId)}
                            className="w-4 h-4 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {(page - 1) * pageSize + index + 1}
                        </td>
                        <td className="px-4 py-3">
                          {emp.dep || emp.fld_department || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {emp.empcode || emp.fld_employeecode}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {emp.empname || emp.fld_employee}
                        </td>
                        <td className="px-4 py-3">
                          {emp.desig || emp.fld_designation || "-"}
                        </td>
                        <td className="px-4 py-3">
                          {emp.branchname || emp.fld_branchname || selectedBranch}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleEdit(empId)}
                            className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Showing{" "}
                {totalRecords === 0 ? 0 : (page - 1) * pageSize + 1} to{" "}
                {Math.min(page * pageSize, totalRecords)} of {totalRecords} results
              </div>

              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page <= 1}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page <= 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>

                <span className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border-x border-gray-300 dark:border-gray-700">
                  Page {page} of {totalPages}
                </span>

                <button
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page >= totalPages}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page >= totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <ConfirmModal
  open={showDeleteConfirm}
  title="Delete Employees"
  message={`Are you sure you want to delete ${selectedIds.length} employee(s)? This action cannot be undone.`}
  confirmLabel="Delete"
  onConfirm={confirmDelete}
  onCancel={() => setShowDeleteConfirm(false)}
/>
    </LayoutWrapper>
  );
};

export default EmployeeDetailListing;